package com.pincer.client

import com.pincer.core.contracts.BaseContract
import com.pincer.core.contracts.BaseContractFromRequest
import com.pincer.core.contracts.RequestContractFromString
import com.pincer.core.Logger
import com.pincer.core.PincerException
import com.pincer.core.CycleSecret
import com.pincer.core.StaleSid
import com.pincer.core.YouNeedToLogIn
import com.pincer.core.YouAreNotAllowed
import com.pincer.core.model.Device
import com.pincer.core.model.PincerPack
import com.pincer.core.model.tree.Pincer
import com.pincer.core.model.tree.Participant
import com.pincer.core.model.patches.PincerPatch
import com.pincer.core.model.drafts.PincerDraft
import com.pincer.core.model.drafts.ParticipantDraft
import com.pincer.core.functions.validateServerUrl
import com.pincer.core.functions.validateWebSocketUrl
import com.pincer.core.functions.validateLocale
import com.pincer.core.functions.validateZone
import com.pincer.core.functions.toPersonalPid
import com.pincer.core.functions.buildJwt
import com.pincer.core.multiplatform.mpName
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.delay
import kotlin.js.JsName

class Client private constructor(
    val deviceId: String,
    var deviceSecret: String,
    var principalId: String?,
    private val log: Logger,
    private val jobCentre: JobCentre,
    private val contract: BaseContract,
    private val webSocketContract: BaseContract?,
    private val webSocket: WebSocket?,
) {

    private val changes: Changes = Changes(log)

    fun listenToChanges(onChange: OnChange) = changes.listen(onChange)

    var onDeviceSecretChange: (String) -> Unit = { _ -> }

    private val pincerPacks = mutableMapOf<String,PincerPack>()

    companion object {

        @JsName("build")
        suspend fun build(
            log: Logger,
            coroutineScope: CoroutineScope,
            serverUrl: String? = null,
            webSocketUrl: String? = null,
            deviceId: String? = null,
            deviceSecret: String? = null,
            // Tests only: talk to this contract instead of a server (no websocket)
            contract: BaseContract? = null,
        ): Client {
            log.i("------------- Pincer Client Starting ------------")
            log.i("Multiplatform : ${mpName()}")
            if (serverUrl != null &&  validateServerUrl(serverUrl) != null) throw Exception("Problem with the serverUrl $serverUrl")
            if (webSocketUrl != null && validateWebSocketUrl(webSocketUrl) != null) throw Exception("Problem with the webSocketUrl $webSocketUrl")
            val jobCentre = JobCentre(log = log, coroutineScope = coroutineScope)
            var webSocket: WebSocket? = null
            var webSocketContract: BaseContract? = null
            val given = contract
            lateinit var contract: BaseContract
            if (given != null) {
                contract = given
            }
            else if (webSocketUrl == null) {
                log.w("No WebSocketUrl specified, realtime updates won't be available")
            }
            else {
                log.i("WebSocketUrl specified: $webSocketUrl Setting up")
                webSocket = WebSocket(url = webSocketUrl, jobCentre = jobCentre, log = log)
                webSocket.start()
                log.i("Setting up websocket contract, from request, from websocket")
                webSocketContract = BaseContractFromRequest(RequestContractFromWebSocket(webSocket))
            }
            val serverUrlOrDefault = serverUrl ?: DEFAULT_SERVER_URL
            if (given != null) {
                log.i("Using the contract given")
            }
            else if (serverUrlOrDefault == webSocketUrl) {
                log.i("Using WebSocket contrtact for everything")
                contract = webSocketContract!!
            }
            else {
                log.i("Setting up base contract, from request, from http ($serverUrlOrDefault)")
                contract = BaseContractFromRequest(RequestContractFromString(StringContractFromHttp(serverUrlOrDefault)))
            }
            lateinit var useDeviceId: String
            lateinit var useDeviceSecret: String
            var principalId: String? = null
            if (deviceId != null) {
                if (deviceSecret == null) throw Exception("Device secret must be given alongside the device id")
                log.i("Noting device id ($deviceId) and initial secret. Also checking principalWhoAmI.")
                useDeviceId = deviceId
                useDeviceSecret = deviceSecret
                try {
                    try {
                        principalId = contract.principalWhoAmI(buildJwt(iss = deviceId, sub = null, secret = deviceSecret))
                        log.i("Discovered principalId $principalId")
                    }
                    catch (cycleSecret: CycleSecret) {
                        log.i("Caught CycleSecret on initial principalWhoAmI. Updating and trying again.")
                        useDeviceSecret = cycleSecret.message
                        principalId = contract.principalWhoAmI(buildJwt(iss = deviceId, sub = null, secret = useDeviceSecret))
                        log.i("Discovered principalId $principalId")
                    }
                }
                catch (yntli: YouNeedToLogIn) {
                    log.i("No principalId from server")
                }
            }
            else {
                log.i("Registering new device")
                val device = contract.device()
                log.i("New device ${device.id} / ${device.secretHidden()}")
                useDeviceId = device.id
                useDeviceSecret = device.secret
            }
            val client = Client(
                deviceId = useDeviceId,
                deviceSecret = useDeviceSecret,
                principalId = principalId,
                log = log.logger(withDeviceId = deviceId, withPrincipalId = principalId),
                jobCentre = jobCentre,
                contract = contract,
                webSocketContract = webSocketContract,
                webSocket = webSocket,
            )
            if (webSocket != null) {
                webSocket.registerListener(permanent = true, client::comsumeWebSocketMessage)
                client.heartbeats()
            }
            if (principalId != null) client.load(toPersonalPid(principalId))
            log.i("Pincer client started")
            return client
        }

    }

    /*
    Interaction with the two contracts is mediated by this wrapping.
    Each of the contract level methods is made available with a repeated call in case of secret cycling.
    */

    private suspend fun <T> wrap(f:suspend () -> T): T {
        try {
            return f()
        }
        catch (cycleSecret: CycleSecret) {
            log.i("Caught CycleSecret. Updating and trying again.")
            deviceSecret = cycleSecret.message
            onDeviceSecretChange(deviceSecret)
            return f()
        }
        catch (youNeedToLogIn: YouNeedToLogIn) {
            if (principalId == null) {
                log.w("Rethrowing YouNeedToLogIn when no principalId - why didn't checks avoid this?")
            }
            else {
                log.w("YouNeedToLogIn - logging out and rethrowing")
                logoutInner()
            }
            throw youNeedToLogIn
        }
        catch (youAreNotAllowed: YouAreNotAllowed) {
            // Originally considered logoutInner here too, but seems unfair e.g. people who
            // submit an alteration based on a stale view of their permissions.
            log.w("YouAreNotAllowed - rethrowing")
            throw youAreNotAllowed
        }
    }

    // Most of the contract level calls need a jwt
    private fun jwt(): String = buildJwt(iss = deviceId, sub = principalId, secret = deviceSecret)

    fun logJwt() = log.i("JWT: ${jwt()}")

    // Here we fulfill the contract methods
    private suspend fun _device(): Device
        = contract.device() // No wrap needed here
    private suspend fun _principalAnonymous(locale: String?, zone: String?)
        = wrap w@{ return@w contract.principalAnonymous(jwt(), locale, zone) }
    private suspend fun _principalTriggerCode(credential: String)
        = wrap w@{ return@w contract.principalTriggerCode(jwt(), credential) }
    private suspend fun _principalConfirmCode(credential: String, code: String, locale: String?, zone: String?): String
        = wrap w@{ return@w contract.principalConfirmCode(jwt(), credential, code, locale, zone) }
    private suspend fun _principalLogout(allDevices: Boolean)
        = wrap w@{ return@w contract.principalLogout(jwt(), allDevices) }
    private suspend fun _principalWhoAmI(): String
        = wrap w@{ return@w (webSocketContract ?: contract).principalWhoAmI(jwt()) } // Note we use of webSocketContract if available
    private suspend fun _get(pid: String): Pincer
        = wrap w@{ return@w contract.get(jwt(), pid) }
    private suspend fun _getAt(pid: String, sid: Long): Pincer
        = wrap w@{ return@w contract.getAt(jwt(), pid, sid) }
    private suspend fun _getSince(pid: String, sid: Long): PincerPatch
        = wrap w@{ return@w contract.getSince(jwt(), pid, sid) }
    private suspend fun _patch(pid: String, sid: Long, patch: PincerPatch): PincerPatch
        = wrap w@{ return@w contract.patch(jwt(), pid, sid, patch) }
    private suspend fun _fork(pid: String): String
        = wrap w@{ return@w contract.fork(jwt(), pid) }

    suspend fun heartbeats() = jobCentre.launch("heartbeats") {
        log.i("Starting heartbeat loop")
        if (webSocketContract == null) throw Exception("WebSocketContract not in place")
        while (true) {
            if (principalId == null) {
                //log.f("Not doing heartbeat as not logged in")
            }
            else {
                //log.f("Heartbeat")
                // TODO think about a less expensive option for the server - signed jwts?
                try {
                    val principalIdFromServer = _principalWhoAmI()
                    if (principalId != principalIdFromServer) {
                        log.w("Principal change on principalWhoAmI. Logging out.")
                        logoutInner()
                    }
                }
                catch (exception: Exception) {
                    log.w("Heartbeat principalWhoAmI failed", exception)
                }
            }
            // WebSockets seem to stick around by themselves for 30 seconds, so we beat every 20
            delay(20*1000)
        }
    }

    private suspend fun logoutInner() {
        principalId = null
        jobCentre.cancelAllJobs()
        pincerPacks.clear()
    }

    fun loggedIn() = principalId != null
    fun loggedOut() = !loggedIn()
    fun assertLoggedIn()  { if (loggedOut()) throw Exception("Not logged in") }
    fun assertLoggedOut() { if (loggedIn())  throw Exception("Not logged out") }

    suspend fun principalAnonymous(locale: String?, zone: String?): String {
        log.i("Client principalAnonymous locale:$locale zone:$zone")
        assertLoggedOut()
        val pi = _principalAnonymous(locale, zone)
        principalId = pi
        // As for confirming a code: register this device for changes now, not at the first heartbeat (20 seconds
        // on), or the pushes in between (someone joining by link misses Roz's first suggestions) are lost
        log.i("principalAnonymous returned principalId:$pi - triggering whoAmI to register for changes.")
        _principalWhoAmI()
        log.i("Now loading personal pincer.")
        load(toPersonalPid(pi))
        return pi
    }

    suspend fun principalTriggerCode(credential: String) {
        log.i("Client principalTriggerCode for $credential")
        _principalTriggerCode(credential)
    }

    suspend fun principalConfirmCode(credential: String, code: String, locale: String?, zone: String?): String {
        log.i("Client principalConfirmCode credential:$credential code:$code locale:$locale zone:$zone")
        val pi = _principalConfirmCode(credential, code, locale, zone)
        principalId = pi
        log.i("Confirm code returned principalId:$pi - triggering whoAmI to register for changes.")
        val me =_principalWhoAmI()
        log.i("I am $me. Now loading personal pincer.")
        load(toPersonalPid(pi))
        return pi
    }

    suspend fun principalLogout(allDevices: Boolean): Unit {
        log.i("Client logout allDevices:$allDevices")
        assertLoggedIn()
        logoutInner()
        _principalLogout(allDevices)
    }

    suspend fun load(pid: String) {
        log.i("Client load $pid")
        val pp = PincerPack(_get(pid))
        pincerPacks[pid] = pp
        log.i("Load completed, pid:${pp.local.pid} sid:${pp.local.sid} created:${pp.local.createdAt}")
        changes.emit()
    }

    fun pincer(pid: String?): Pincer?                 = if (pid == null)         null else pincerPacks[pid]?.local
    fun pincerUi(pid: String?): PincerDraft?          = if (pid == null)         null else pincerPacks[pid]?.ui
    fun pincerSelf(pid: String?): Participant?        = if (principalId == null) null else pincer(pid)?.participants?.get(principalId!!)
    fun pincerUiSelf(pid: String?): ParticipantDraft? = if (principalId == null) null else pincerUi(pid)?.getOrAddParticipant(principalId!!)

    fun personalPincer(): Pincer?                     = if (principalId == null) null else pincer(toPersonalPid(principalId!!))
    fun personalUi(): PincerDraft?                    = if (principalId == null) null else pincerUi(toPersonalPid(principalId!!))
    fun personalSelf(): Participant?                  = if (principalId == null) null else pincerSelf(toPersonalPid(principalId!!))
    fun personalUiSelf(): ParticipantDraft?           = if (principalId == null) null else pincerUiSelf(toPersonalPid(principalId!!))

    private suspend fun wrapSync(pid: String, f:(suspend (PincerPack)->Unit)) {
        if (pincerPacks[pid] == null) {
            log.w("Call to sync before load, $pid")
            throw Exception("Not yet loaded")
            // or we could just instead do it for them ...?
            // load(pid)
        }
        val pincerPack = pincerPacks[pid]!!
        if (pincerPack.syncing) {
            // Until October 2026 this call was dropped, and with it a change made while a patch was in flight
            // (a chat sent just after a name change never left the device)
            log.i("Already syncing, will sync again after")
            pincerPack.syncAgain = true
            return
        }
        if (pincerPack.failed()) {
            log.w("No Sync. Already failed")
            return
        }
        try {
            pincerPack.syncing = true
            f(pincerPack)
            while (pincerPack.syncAgain) {
                pincerPack.syncAgain = false
                log.i("Syncing again: ${pincerPack.local.pid}")
                _sync(pincerPack)
            }
            pincerPack.syncing = false
        }
        catch (e: Exception) {
            pincerPack.syncing = false
            pincerPack.failure = e.message
            log.w("Pincer Sync failed ...")
            log.e(e.stackTraceToString())
        }
        changes.emit()
    }

    suspend fun syncUp(pid: String) = wrapSync(pid) { pincerPack ->
        log.i("SyncUp: ${pid}")
        val patch = pincerPack.local.diff(pincerPack.remote)
        if (patch.isEmpty()) {
            log.w("No action on syncUp as local == remote")
        }
        else {
            try {
                // What was sent, so changes made while the patch is in flight can be kept (and sent next)
                val sent = pincerPack.local.copyViaJson()
                val remotePatch = _patch(pid = pid, sid = pincerPack.remote.sid, patch = patch)
                log.i("SyncUp patch succeeded, sid from server: ${remotePatch.sid}")
                pincerPack.remote.applyPatch(remotePatch)
                val madeMeanwhile = pincerPack.local.diff(sent)
                pincerPack.local.applyPatch(pincerPack.remote.diff(pincerPack.local))
                if (!madeMeanwhile.isEmpty()) {
                    pincerPack.local.applyPatch(madeMeanwhile)
                    pincerPack.syncAgain = true
                }
            }
            catch (ss: StaleSid) {
                log.w("StaleSid while optimistically trying syncUp. Starting full sync instead.")
                _sync(pincerPack)
            }
        }
    }

    suspend fun sync(pid: String) = wrapSync(pid) { pincerPack ->
        log.i("Sync: ${pid}")
        _sync(pincerPack)
    }

    private suspend fun _sync(pincerPack: PincerPack) {
        val local = pincerPack.local
        val remote = pincerPack.remote
        //log.f("Local: ${local}")
        //log.f("Remote: ${remote}")
        val remotePatch = _getSince(remote.pid, remote.sid)
        val localPatch = local.diff(remote)
        val localPatchIsEmpty = localPatch.isEmpty()
        val remotePatchIsEmpty = remotePatch.isEmpty()
        if (remotePatchIsEmpty && localPatchIsEmpty) {
            log.w("Patches empty, no action taken")
        }
        else {
            if (localPatchIsEmpty) {
                //log.f("Local patch : ${localPatch.json()}")
            }
            if (remotePatchIsEmpty) {
                //log.f("Remote patch : ${remotePatch.json()}")
            }
            // Check for conflicts
            val remotePatchFirst: Pincer = remote.copyViaJson()
            remotePatchFirst.applyPatch(remotePatch)
            remotePatchFirst.applyPatch(localPatch)
            val localPatchFirst: Pincer = remote.copyViaJson()
            localPatchFirst.applyPatch(localPatch)
            localPatchFirst.applyPatch(remotePatch)
            if (remotePatchFirst != localPatchFirst) {
                log.w("Conflicts ... ")
                log.i(localPatchFirst.json())
                log.i(remotePatchFirst.json())
                throw Exception("Conflict")
            }
            local.applyPatch(remotePatch)
            remote.applyPatch(remotePatch)
            // What's left to send once the server's changes are in. It can be nothing: Roz may already have made the
            // same change (filling in a name), and sending an empty patch used to fail the pincer for good
            val toSend = local.diff(remote)
            if (!toSend.isEmpty()) {
                val sent = local.copyViaJson()
                val serverPatch = _patch(pid = remote.pid, sid = remote.sid, patch = toSend)
                if (serverPatch.isEmpty()) {
                    throw PincerException("Looks like patch had no effect. Permissions issue?")
                }
                remote.applyPatch(serverPatch)
                // As in syncUp: keep what changed while the patch was in flight, and send it next
                val madeMeanwhile = local.diff(sent)
                local.applyPatch(serverPatch)
                if (!madeMeanwhile.isEmpty()) {
                    local.applyPatch(madeMeanwhile)
                    pincerPack.syncAgain = true
                }
            }
        }
        //log.f("After sync local: ${local}")
        //log.f("After sync remote: ${remote}")
        //log.f("===================================")
    }

    suspend fun syncPersonal() = sync(toPersonalPid(principalId!!))
    suspend fun syncUpPersonal() = syncUp(toPersonalPid(principalId!!))

    fun unload(pid: String) {
        if (pincerPacks[pid] == null) {
            log.w("Call to unload, but no pack present $pid")
        }
        else {
            log.i("Dropping pack $pid")
            pincerPacks.remove(pid)
        }
    }

    suspend fun fork(pid: String): String {
        log.i("Client forking $pid")
        val newPid = _fork(pid)
        log.i("Fork succeeded, new pid: $newPid")
        load(newPid)
        return newPid
    }

    suspend fun stop() {
        log.i("Pincer client stopping")
        logoutInner()
        webSocket?.stop()
        log.i("------------- Pincer Client Stopped ------------")
    }

    fun anyPacksFailed(): Boolean {
        for (pincerPack in pincerPacks.values) {
            if (pincerPack.failed()) return true
        }
        return false
    }

    private fun comsumeWebSocketMessage(message: String): Boolean {
        val personal = personalPincer()
        if (personal == null) {
            log.w("Ignoring websocket message as personal pincer not there : $message")
            return false;
        }
        if (message != personal.pid) {
            log.w("Ignoring websocket message, not personal pid : $message")
            return false;
        }
        jobCentre.launch("personal-sync") {
            log.i("Took signal of change on personal pincer, sycning")
            val hashesBefore = personal.participations.map{ it.key to it.value.hash }.toMap()
            //log.f("Hashes before : $hashesBefore")
            syncPersonal()
            for (pid in personal.participations.keys) {
                if (hashesBefore[pid] != personal.participations[pid]!!.hash) {
                    val pincerPack = pincerPacks.get(pid)
                    if (pincerPack == null) {
                        log.i("Ingoring change, not loaded: $pid")
                    }
                    else {
                        log.i("Change spotted on : $pid, syncing")
                        sync(pid)
                    }
                }
            }
        }
        return true
    }

}
